import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import subcategoryApi from '../../api/subcategoryApi';

/* ------------------------------------------------------------------ */
/* Async Thunks */
/* ------------------------------------------------------------------ */

export const fetchSubcategories = createAsyncThunk(
  'subcategory/fetchAll',
  async ({ page = 1, pageSize = 10, search = '' }, { rejectWithValue }) => {
    try {
      const res = await subcategoryApi.getAll(page, pageSize, search);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data?.errors?.[0] || 'Failed to load');
    }
  }
);

export const createSubcategory = createAsyncThunk(
  'subcategory/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await subcategoryApi.create(data);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const updateSubcategory = createAsyncThunk(
  'subcategory/update',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await subcategoryApi.update(id, data);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const deleteSubcategory = createAsyncThunk(
  'subcategory/delete',
  async (id, { rejectWithValue }) => {
    try {
      await subcategoryApi.delete(id);
      return id;
    } catch (e) {
      return rejectWithValue(e.response?.data?.errors?.[0] || 'Delete failed');
    }
  }
);

/* -------------------------- EXPORT -------------------------- */
export const exportSubcategories = createAsyncThunk(
  'subcategory/export',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await subcategoryApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subcategories_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

/* -------------------------- IMPORT PREVIEW -------------------------- */
export const importPreview = createAsyncThunk(
  'subcategory/importPreview',
  async (file, { rejectWithValue }) => {
    try {
      const data = await subcategoryApi.importPreview(file);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Preview failed');
    }
  }
);

/* -------------------------- IMPORT CONFIRM -------------------------- */
export const importConfirm = createAsyncThunk(
  'subcategory/importConfirm',
  async (preview, { rejectWithValue, dispatch }) => {
    try {
      await subcategoryApi.importConfirm(preview);
      dispatch(fetchSubcategories({ page: 1, pageSize: 10 }));
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Import failed');
    }
  }
);

/* ------------------------------------------------------------------ */
/* Slice */
/* ------------------------------------------------------------------ */

const subcategorySlice = createSlice({
  name: 'subcategory',
  initialState: {
    subcategories: [],
    pagination: { next: null, previous: null, count: 0, page_size: 10 },
    isLoading: false,
    error: null,
    importPreview: null,
    importTotal: null,
  },
  reducers: {
    clearError: (s) => { s.error = null; },
  },
  extraReducers: (b) => {
    // FETCH
    b.addCase(fetchSubcategories.pending, (s) => { s.isLoading = true; })
      .addCase(fetchSubcategories.fulfilled, (s, a) => {
        s.isLoading = false;
        s.subcategories = a.payload.data;
        s.pagination = a.payload.pagination;
      })
      .addCase(fetchSubcategories.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      });

    // CREATE
    b.addCase(createSubcategory.fulfilled, (s, a) => {
      s.subcategories.unshift(a.payload);
      s.pagination.count += 1;
    });

    // UPDATE
    b.addCase(updateSubcategory.fulfilled, (s, a) => {
      const idx = s.subcategories.findIndex(x => x.id === a.payload.id);
      if (idx !== -1) s.subcategories[idx] = a.payload;
    });

    // DELETE
    b.addCase(deleteSubcategory.fulfilled, (s, a) => {
      s.subcategories = s.subcategories.filter(x => x.id !== a.payload);
      s.pagination.count -= 1;
    });

    // EXPORT
    b.addCase(exportSubcategories.rejected, (s, a) => {
      s.error = a.payload;
    });

    // IMPORT PREVIEW
    b.addCase(importPreview.pending, (s) => { s.isLoading = true; })
      .addCase(importPreview.fulfilled, (s, a) => {
        s.isLoading = false;
        s.importPreview = a.payload.preview;
        s.importTotal = a.payload.total;
      })
      .addCase(importPreview.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      });

    // IMPORT CONFIRM
    b.addCase(importConfirm.pending, (s) => { s.isLoading = true; })
      .addCase(importConfirm.fulfilled, (s) => {
        s.isLoading = false;
        s.importPreview = null;
        s.importTotal = null;
      })
      .addCase(importConfirm.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      });

    // Shared pending/rejected
    ['createSubcategory', 'updateSubcategory', 'deleteSubcategory'].forEach(act => {
      b.addCase(act + '.pending', (s) => { s.isLoading = true; })
        .addCase(act + '.rejected', (s, a) => {
          s.isLoading = false;
          s.error = a.payload;
        });
    });
  },
});

export const { clearError } = subcategorySlice.actions;
export default subcategorySlice.reducer;